/**
 * convertCostDataToExcel
 * Converts the furniture cost estimation object into a multi-sheet Excel structure.
 * Compatible with SheetJS (xlsx) library.
 *
 * Usage:
 *   import * as XLSX from 'xlsx';
 *   const workbook = convertCostDataToExcel(data);
 *   XLSX.writeFile(workbook, 'cost_report.xlsx');
 *
 * @param {Object} data - The cost estimation object
 * @returns {Object} SheetJS workbook object
 */

import * as XLSX from 'xlsx';

// ─── Helpers ────────────────────────────────────────────────────────────────

const colCategory = 'Category';
const colName = 'Name';
const colUnit = 'Unit';
const colUnitPrice = 'Unit Price (VNĐ)';
const colTotalQuantity = 'Total Quantity';
const colTotalCost = 'Total Cost (VNĐ)';
const colTotalSale = 'Total Sale (VNĐ)';
const colProfitMargin = 'Profit Margin';

// const DEFAULT_NAME = {
//   doors: {
//     acrylic: 'Cửa Acrylic',
//     normal: {
//       dulux: 'Cửa trơn sơn 2k',
//     }, //
//     alum: 'Cửa nhôm',
//     glassInside: 'Kính',
//     noGrainPano: {
//       dulux: 'Cửa Pano sơn 2k',
//       melamine: 'Cửa Pano melamine không vân',
//     },
//     grainPano: {
//       melamine: 'Cửa Pano melamine có vân',
//     },
//     louver: {
//       dulux: 'Cửa lá sách sơn 2k',
//       melamine: 'Cửa lá sách sơn melamine',
//     },
//     milled: {
//       dulux: 'Cửa phay ngang/dọc sơn 2k',
//       melamine: 'Cửa phay ngang/dọc sơn melamine',
//     },
//     drilled: {
//       dulux: 'Cửa đục lỗ sơn 2k',
//       melamine: 'Cửa đục lỗ sơn melamin',
//     },
//   },
//   materials: {
//     boards: 'Vật liệu - ván',
//     acrylicCabinetTrims: 'Vật liệu - Nẹp Acrylic',
//     edgeBanding: {
//       normal: 'Vật liệu - chỉ dán cạnh thường 17mm',
//       diff: 'Vật liệu - chỉ dán cạnh 34mm',
//       bevel: 'Vật liệu - chỉ dán cạnh vát 45 độ',
//     },
//   },
//   component: {
//     hinges: 'Linh kiện - bản lề thường',
//     hinges135: 'Linh kiện - bản lề 135',
//     connectors: 'Linh kiện - chốt cam',
//     rails: 'Linh kiện - ray',
//     liftSupport: 'Linh kiện - ti hơi',
//   },
//   accessory: {
//     handles: 'Phụ kiện - tay nắm',
//     leds: 'Phụ kiện - LEDS',
//     ledsDecor: 'Phụ kiện - LED decor',
//     lightSwitches: 'Phụ kiện - công tắc',
//     ledDrivers: 'Phụ kiện - nguồn đèn LED',
//     clothesHangers: 'Phụ kiện - thanh treo đồ',
//     mirrors: 'Phụ kiện - gương',
//     legs: 'Phụ kiện - chân',
//   },
//   labor: {
//     design: 'Công lắp đặt',
//     cnc: 'Công CNC',
//     edgeBanding: {
//       normal: 'Công dán cạnh thường 17mm',
//       diff: 'Công dán cạnh 34mm',
//       bevel: 'Công dán cạnh vát 45 độ',
//     },
//     packing: 'Công đóng gói',
//     painting: 'Công sơn 2k',
//     assembly: {
//       wardrobe: 'Công lắp đặt tủ áo',
//       totl: 'Công lắp đặt tủ ốp tủ lạnh',
//       dry: 'Công lắp đặt tủ đồ kho',
//       kitchenLower: 'Công lắp đặt tủ bếp dưới',
//       kitchenUpper: 'Công lắp đặt tủ bếp trên',
//       kitchenCeiling: 'Công lắp đặt tủ bếp đụng trần',
//       shoeCabinet: 'Công lắp đặt tủ giày',
//       decorative: 'Công lắp đặt tủ trang trí',
//       tv: 'Công lắp đặt tủ tivi',
//       bed: 'Công lắp đặt giường',
//       bedDrawer: 'Công lắp đặt hộc kéo giường',
//       desk: 'Công lắp đặt bàn làm việc',
//       vanity: 'Công lắp đặt bàn trang điểm',
//       shelf: 'Công lắp đặt đợt kệ',
//       shoeBench: 'Công lắp đặt đôn ngồi',
//       wallPanelMelamine: 'Công lắp đặt vách trơn',
//       slatWallPartition: 'Công lắp đặt vách lam',
//       wallPanelMirror: 'Công lắp đặt vách gương',
//       led: 'Công lắp đặt LED',
//       bedLower: 'Công lắp đặt giường bục',
//     },
//   },
//   special: {
//     equipments: 'Thiết bị',
//     countertop: 'Mặt bếp',
//     backsplash: 'Vách bếp',
//     curveBlocks: {
//       'rank1.AC.M': 1000000,
//       'rank1.AC.L': 1500000,
//       'rank1.AC.Plastic': 1200000,
//       'rank1.KES.M': 800000,
//       'rank1.KES.L': 1200000,
//       'rank1.ACC.Plywood': 1200000,
//       'rank2.AC.M': 2000000,
//       'rank2.AC.L': 2500000,
//       'rank2.AC.Plastic': 2500000,
//       'rank2.KES.M': 1500000,
//       'rank2.KES.L': 1800000,
//       'rank2.ACC.Plywood': 2500000,
//       'rank3.AC.M': 3000000,
//       'rank3.AC.L': 3500000,
//       'rank3.AC.Plastic': 3500000,
//       'rank3.KES.M': 2800000,
//       'rank3.KES.L': 3000000,
//       'rank3.ACC.Plywood': 4000000,
//     },
//   },
// };

/**
 * Parses a bilingual JSON name field like '{"vi":"Kính trong","en":"Clear glass"}'
 * Returns the Vietnamese name, falling back to English, then raw string.
 * @param {string|object} raw - The raw name value (string or JSON string)
 * @returns {string} The parsed Vietnamese name or fallback value
 */
const parseName = raw => {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    return parsed.vi ?? parsed.en ?? raw;
  } catch {
    return raw;
  }
};

/**
 * Formats a value to a number, returning 0 if invalid.
 * @param {*} val - The value to format
 * @returns {number} The parsed number or 0 if NaN
 */
const fmt = val => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

/**
 * Converts nested item objects into a flat array of rows.
 * Groups identical items by name and unit price, summing quantities and costs.
 * Handles both array and object (nested) structures recursively.
 * @param {Object|Array} items - Nested item structure to flatten
 * @returns {Array<Object>} Array of flattened row objects with grouped data
 */
const convertItems = items => {
  const rows = [];

  const addItems = (items, category) => {
    // Group by name + unitPrice
    let grouped = {};
    if (!Array.isArray(items)) {
      Object.entries(items).forEach(([key, value]) => {
        const temp = addItems(
          value,
          `${parseName(category)}__${parseName(key)}`
        );
        grouped = { ...grouped, ...temp };
      });
    } else if (!items.length) return;
    else {
      items.forEach(item => {
        const key = `${parseName(item.name ?? category)}__${item.unitPrice}`;
        if (!grouped[key]) {
          grouped[key] = {
            [colCategory]: category,
            [colName]: parseName(item.name),
            [colUnit]: item.unit ?? '',
            [colUnitPrice]: item.unitPrice,
            [colTotalQuantity]: 0,
            [colTotalCost]: 0,
            [colTotalSale]: 0,
            [colProfitMargin]: `${fmt(item.profit) * 100}%`,
          };
        }
        grouped[key][colTotalQuantity] = +(
          grouped[key][colTotalQuantity] + fmt(item.quantity)
        ).toFixed(4);
        grouped[key][colTotalCost] = Math.round(
          grouped[key][colTotalCost] + fmt(item.cost)
        );
        grouped[key][colTotalSale] = Math.round(
          grouped[key][colTotalSale] + fmt(item.sale)
        );
      });
    }

    return grouped;
    // Object.values(grouped).forEach(r => rows.push(r));
  };

  // // glassInside items (the only populated door array in this dataset)
  // addItems(doors.glassInside, 'Glass Inside');

  // // Extendable: add other door types as needed
  // const doorTypes = {
  //   acrylic: 'Acrylic',
  //   alum: 'Aluminium',
  // };
  // Object.entries(doorTypes).forEach(([key, label]) =>
  //   addItems(doors[key], label)
  // );

  Object.entries(items).forEach(([key, value]) => {
    const grouped = addItems(value, key);
    if (grouped == null) {
    } else {
      if (grouped) {
        Object.values(grouped).forEach(r => rows.push(r));
      }
    }
  });

  return rows;
};

/**
 * Builds a summary sheet with aggregated costs and sales per category.
 * Recursively sums costs and sales across all nested items, then adds a TOTAL row.
 * @param {Object} data - Cost data object containing doors, materials, components, etc.
 * @returns {Array<Object>} Array of summary rows with categories and totals
 */
const buildSummary = data => {
  const sumCost = items =>
    (Array.isArray(items) ? items : []).reduce(
      (acc, i) => acc + fmt(i.cost),
      0
    );

  const sumSale = items =>
    (Array.isArray(items) ? items : []).reduce(
      (acc, i) => acc + fmt(i.sale),
      0
    );

  const sumNestedCost = obj => {
    if (!obj || typeof obj !== 'object') return 0;
    return Object.values(obj).reduce((acc, val) => {
      if (Array.isArray(val)) return acc + sumCost(val);
      if (typeof val === 'object') return acc + sumNestedCost(val);
      return acc;
    }, 0);
  };
  const sumNestedSale = obj => {
    if (!obj || typeof obj !== 'object') return 0;
    return Object.values(obj).reduce((acc, val) => {
      if (Array.isArray(val)) return acc + sumSale(val);
      if (typeof val === 'object') return acc + sumNestedSale(val);
      return acc;
    }, 0);
  };

  const categories = [
    {
      [colCategory]: 'Doors',
      [colTotalCost]: Math.round(sumNestedCost(data.doors)),
      [colTotalSale]: Math.round(sumNestedSale(data.doors)),
    },
    {
      [colCategory]: 'Materials – Boards',
      [colTotalCost]: Math.round(sumCost(data.materials?.boards)),
      [colTotalSale]: Math.round(sumSale(data.materials?.boards)),
    },
    {
      [colCategory]: 'Materials – Edge Banding',
      [colTotalCost]: Math.round(sumNestedCost(data.materials?.edgeBanding)),
      [colTotalSale]: Math.round(sumNestedSale(data.materials?.edgeBanding)),
    },
    {
      [colCategory]: 'Components',
      [colTotalCost]: Math.round(sumNestedCost(data.component)),
      [colTotalSale]: Math.round(sumNestedSale(data.component)),
    },
    {
      [colCategory]: 'Accessories',
      [colTotalCost]: Math.round(sumNestedCost(data.accessory)),
      [colTotalSale]: Math.round(sumNestedSale(data.accessory)),
    },
    {
      [colCategory]: 'Labor',
      [colTotalCost]: Math.round(sumNestedCost(data.labor)),
      [colTotalSale]: Math.round(sumNestedSale(data.labor)),
    },
  ];

  const totalSale = categories.reduce((acc, r) => acc + r[colTotalSale], 0);
  const totalCost = categories.reduce((acc, r) => acc + r[colTotalCost], 0);
  categories.push({
    [colCategory]: 'TOTAL',
    [colTotalSale]: totalSale,
    [colTotalCost]: totalCost,
  });

  return categories;
};

/**
 * Merges rows with identical non-numeric values by summing their numeric columns.
 * Flattens the array first, then reduces it by combining rows that have matching
 * category/name/unit values, summing their quantity/cost/sale values.
 * Filters out rows with zero values in all quantity/cost/sale columns.
 * @param {Array<Array<Object>>} rowsArray - Array of row arrays to merge
 * @returns {Array<Object>} Merged array with deduplicated rows and summed values
 */
export const mergeRows = rowsArray => {
  const rows = rowsArray.flat().reduce((prev, curr) => {
    const rows = [...prev];

    let isUpdated = false;
    rows.forEach(row => {
      const keys = Object.keys(row);
      if (
        keys.every(
          key =>
            (typeof row[key] !== 'number' &&
              typeof curr[key] !== 'number' &&
              row[key] === curr[key]) ||
            (typeof row[key] === 'number' && typeof curr[key] === 'number')
        )
      ) {
        keys.forEach(key => {
          if (typeof row[key] === 'number' && typeof curr[key] === 'number') {
            row[key] += curr[key];
          }
        });
        isUpdated = true;
      }
    });
    if (!isUpdated) {
      rows.push({ ...curr });
    }
    return rows;
  }, []);
  return rows.filter(
    row => row[colTotalQuantity] || row[colTotalCost] || row[colTotalSale]
  );
};

// ─── Main Converter ──────────────────────────────────────────────────────────

/**
 * Converts cost data object into a SheetJS workbook with multiple sheets.
 * Creates sheets for summary, doors, materials, components, accessories, labor, and specials.
 * Each sheet is auto-formatted with appropriate column widths.
 *
 * Sheets produced:
 *  1. Summary     — aggregate totals per category
 *  2. Doors       — glass and other door types
 *  3. Materials   — boards and edge banding
 *  4. Components  — hinges, connectors, rails
 *  5. Accessories — handles, hangers, LEDs, etc.
 *  6. Labor       — design, CNC, edge banding, packing, assembly
 *  7. Specials    — special/custom items
 *
 * @param {Object} data - Cost estimation data object with doors, materials, components, etc.
 * @returns {Object} SheetJS workbook object (can be written with XLSX.writeFile)
 */
export const convertCostDataToExcel = data => {
  try {
    const wb = XLSX.utils.book_new();

    const sheets = [
      { name: 'Summary', rows: buildSummary(data) },
      { name: 'Doors', rows: convertItems(data.doors ?? {}) },
      { name: 'Materials', rows: convertItems(data.materials ?? {}) },
      { name: 'Components', rows: convertItems(data.component ?? {}) },
      { name: 'Accessories', rows: convertItems(data.accessory ?? {}) },
      { name: 'Labor', rows: convertItems(data.labor ?? {}) },
      { name: 'Specials', rows: convertItems(data.special ?? {}) },
    ];

    sheets.forEach(({ name, rows }) => {
      const ws = rows.length
        ? XLSX.utils.json_to_sheet(rows)
        : XLSX.utils.aoa_to_sheet([['No data for this section']]);

      // Auto column width — cap at 40 chars
      if (rows.length) {
        const cols = Object.keys(rows[0]);
        ws['!cols'] = cols.map(col => ({
          wch: Math.min(
            40,
            Math.max(
              col.length,
              ...rows.map(r => String(r[col] ?? '').length)
            ) + 2
          ),
        }));
      }

      XLSX.utils.book_append_sheet(wb, ws, name);
    });

    return wb;
  } catch (error) {
    console.error('Error converting cost data to Excel:', error);
    return XLSX.utils.book_new(); // return empty workbook on error
  }
};

/**
 * Converts an array of cost data objects into a SheetJS workbook.
 * Merges summary data from multiple cost datasets into a single Summary sheet.
 * Useful for comparing or aggregating costs across multiple orders/projects.
 *
 * @param {Array<Object>} data - Array of cost estimation data objects
 * @returns {Object} SheetJS workbook object with merged Summary sheet
 * @throws Returns empty workbook on error
 */
export const convertCostMultiDataToExcel = data => {
  try {
    const wb = XLSX.utils.book_new();

    if (!Array.isArray(data)) return;

    const sheets = [
      {
        name: 'Summary',
        rows: mergeRows(data.map(item => buildSummary(item))),
      },
      {
        name: 'Cửa tủ',
        rows: mergeRows(data.map(item => convertItems(item.doors))),
      },
      {
        name: 'Vật liệu',
        rows: mergeRows(data.map(item => convertItems(item.materials))),
      },
      {
        name: 'Linh kiện',
        rows: mergeRows(data.map(item => convertItems(item.component))),
      },
      {
        name: 'Phụ kiện',
        rows: mergeRows(data.map(item => convertItems(item.accessory))),
      },
      {
        name: 'Công thợ',
        rows: mergeRows(data.map(item => convertItems(item.labor))),
      },
      {
        name: 'Khác',
        rows: mergeRows(data.map(item => convertItems(item.special))),
      },
    ];

    sheets.forEach(({ name, rows }) => {
      const ws = rows.length
        ? XLSX.utils.json_to_sheet(rows)
        : XLSX.utils.aoa_to_sheet([['No data for this section']]);

      // Auto column width — cap at 40 chars
      if (rows.length) {
        const cols = Object.keys(rows[0]);
        ws['!cols'] = cols.map(col => ({
          wch: Math.min(
            40,
            Math.max(
              col.length,
              ...rows.map(r => String(r[col] ?? '').length)
            ) + 2
          ),
        }));
      }

      XLSX.utils.book_append_sheet(wb, ws, name);
    });

    return wb;
  } catch (error) {
    console.error('Error converting cost data to Excel:', error);
    return XLSX.utils.book_new(); // return empty workbook on error
  }
};
